// store/useAnonStore.ts
import { createSlice, Dispatch, PayloadAction, UnknownAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "./index";
import { v4 as uuidv4 } from "uuid";

export interface AnonState {
  anonSessionId: string;
  distritoId: number;
}

const initialState: AnonState = {
  anonSessionId: "",
  distritoId: 0,
};

export const anonSlice = createSlice({
  name: "anon",
  initialState,
  reducers: {
    setAnonSessionId(state, a: PayloadAction<string>) {
      state.anonSessionId = a.payload ?? "";
    },
    clearAnon(state) {
      state.anonSessionId = "";
      state.distritoId = 0;
    },
    setDistritoId(state, a: PayloadAction<number>) {
      state.distritoId = a.payload ?? 0;
    },
  },
});

export const { setAnonSessionId, clearAnon, setDistritoId } = anonSlice.actions;
export default anonSlice.reducer;

// selectors
export const selectAnonSessionId = (s: RootState) => s.anon?.anonSessionId ?? "";
export const selectAnonDistritoId = (s: RootState) => s.anon?.distritoId ?? 0;

function ensureAnonSessionIdCore(
  currentId: string,
  dispatch: Dispatch<UnknownAction>
): string {
  if (typeof currentId === 'string' && currentId.length > 0) return currentId;
  const id = uuidv4();
  dispatch(setAnonSessionId(id));
  return id;
}

// facade hook

export function useAnonStore() {
  const dispatch = useDispatch<AppDispatch>();
  let anonSessionId = useSelector(selectAnonSessionId);
  const distritoId = useSelector(selectAnonDistritoId);

  // Eagerly create anonSessionId if missing
  if (!anonSessionId) {
    anonSessionId = ensureAnonSessionIdCore(anonSessionId, dispatch);
  }

  const getAnonSessionId = useCallback(
    () => ensureAnonSessionIdCore(anonSessionId, dispatch),
    [anonSessionId, dispatch]
  );

  const set = useCallback(
    (id: string) => dispatch(setAnonSessionId(id)),
    [dispatch]
  );
  const clear = useCallback(() => dispatch(clearAnon()), [dispatch]);

  const setDistrict = useCallback(
    (id: number) => dispatch(setDistritoId(id)),
    [dispatch]
  );
  // No separate clear for district; use clearAnon instead

  return {
    anonSessionId,
    distritoId,
    getAnonSessionId,
    setAnonSessionId: set,
    clearAnon: clear,
    setDistritoId: setDistrict,
  } as const;
}

export function ensureAnonSessionIdFrom(state: RootState, dispatch: Dispatch<UnknownAction>): string {
  return ensureAnonSessionIdCore(selectAnonSessionId(state), dispatch);
}